import Big from "big.js";
import shortid from "shortid";
import * as AccountingUtils from "../AccountingUtils";
import * as PSHelper from "../ProfessionalServiceHelper";
import ItemType from "../reference/ItemType";
import { Period, Utils, moment } from "revlock-webutils";
import { RecognitionRule } from "../Reference";
import { Codes } from "../ErrorCode";
import {
    getAutoCreateSSP,
    prefillZeroAtStart,
    buildPlan,
    calculatePlanAmount
} from "../ArrangementUtils";
import PointInTimePlan from "../plan/PointInTimePlan";
import _ from "lodash";

export default class RevenueArrangementUtils {
    static populateClientAttributes(clientAttributes, entities, params) {
        clientAttributes &&
            entities &&
            entities.forEach((entity) => {
                clientAttributes = clientAttributes.filter(
                    (attribute) => attribute.entity == entity
                );

                let attributes = {};

                clientAttributes.forEach((attribute) => {
                    attributes[attribute.name] =
                        AccountingUtils.evaluateExpression(
                            attribute.expression,
                            params
                        );
                });

                params[entity].attributes = Object.assign(
                    {},
                    params[entity].attributes,
                    attributes
                );
            });
    }

    static isCreditNoteItem(soi) {
        return (
            soi.invoiceNumber &&
            soi.referenceInvoiceNumber &&
            soi.invoiceNumber !== soi.referenceInvoiceNumber
        );
    }

    static computeTermAmount(product, startDate, endDate, amount) {
        if (!product) return null;

        const { recurring } = product;

        if (!recurring || !product.listPricePeriodUnit) return amount;

        const { listPricePeriodUnit, listPricePeriod } = product;

        if (!amount) return null;

        const daysPerYear = 365;
        const daysPerMonth = new Big(365).div(12);
        const daysPerQuarter = new Big(365).div(4);

        let dayPrice;

        switch (listPricePeriodUnit) {
            case "years":
                dayPrice = new Big(amount)
                    .div(listPricePeriod)
                    .div(daysPerYear);
                break;
            case "months":
                dayPrice = new Big(amount)
                    .div(listPricePeriod)
                    .div(daysPerMonth);
                break;
            case "quarters":
                dayPrice = new Big(amount)
                    .div(listPricePeriod)
                    .div(daysPerQuarter);
                break;
        }

        function termInDays() {
            if (startDate && endDate) {
                const start = moment(startDate, "YYYY-MM-DD");
                const end = moment(endDate, "YYYY-MM-DD");

                if (
                    start.date() == end.date() &&
                    start.month() == end.month()
                ) {
                    return end.diff(start, "years") * 365;
                }
                //if you are on the same day/month combo then go ahead and just compute flatly against year

                return end.diff(start, "days");
                //otherwise compute exact days
            }

            if (product.term) {
                return new Big(product.term).times(daysPerMonth);
            }
            //convert term in months as specified into days

            return daysPerMonth;
            //assume that the term is 1 month if not specified for a recurring product
        }

        const term = termInDays();

        const listPrice = dayPrice.times(term).toFixed(4);
        return Number(listPrice);
    }

    static newRevenueArrangementItem(
        salesOrderItem,
        revenueArrangementId,
        itemType = ItemType.UNCHANGED
    ) {
        return {
            id: shortid.generate(),
            salesOrderItemId: salesOrderItem.id,
            salesOrderItem: salesOrderItem,
            itemType,
            expenseItems: [],
            revenueArrangementId: revenueArrangementId,
            liabilityAccountId: salesOrderItem.deferredRevenueAccountNumber,
            revenueAccountId: salesOrderItem.revenueAccountNumber,
            contraRevenueAccountId: salesOrderItem.contraRevenueAccountNumber,
            deferredCommissionAccountId:
                salesOrderItem.deferredCommissionAccountNumber,
            commissionAmortizationAccountId:
                salesOrderItem.commissionAccountNumber,
            contraCommissionExpenseAccountId:
                salesOrderItem.contraCommissionExpenseAccountNumber,
            commissionAccumulatedAmortizationAccountId:
                salesOrderItem.commissionAccumulatedAmortizationAccountNumber
        };
    }

    static buildPSPlan(
        item,
        salesOrder,
        revenueArrangement,
        errors,
        calendarConfig,
        orgConfig,
        orgConfigByKey,
        computePreModPlan = false
    ) {
        // Taking backup of original Plan
        if (!item.originalPlan) {
            item.originalPlan = Utils.copy(item.plan);
        }

        const enableBetaFeatures =
            (orgConfigByKey["properties/enableBetaFeatures"] &&
                orgConfigByKey["properties/enableBetaFeatures"].value) ||
            false;

        const incrementalPercentComplete =
            (orgConfigByKey[
                "properties/service-delivery/percentage-incremental"
            ] &&
                orgConfigByKey[
                    "properties/service-delivery/percentage-incremental"
                ].value) ||
            false;

        const revenueWithPPA =
            (orgConfigByKey["properties/enable_prior_period_adj"] &&
                orgConfigByKey["properties/enable_prior_period_adj"].value) ||
            false;

        PSHelper.updatePSPlan(
            item,
            salesOrder,
            revenueArrangement,
            errors,
            enableBetaFeatures,
            calendarConfig,
            incrementalPercentComplete,
            orgConfig,
            revenueWithPPA,
            computePreModPlan
        );
    }

    static calculateProductRevenueByCreditNote(
        creditNoteRais,
        item,
        totalESP,
        totalBookingToAllocate,
        foundNonZeroSSP
    ) {
        let percentOfTotalInvoiceLineItem = 0;
        for (const creditNoteRai of creditNoteRais) {
            percentOfTotalInvoiceLineItem +=
                creditNoteRai.percentOfTotalInvoiceLineItem;
        }
        let relativeSSPProportion =
            item.extendedEstimatedSalePrice / totalESP || 0;

        // non credit note line item is always considered 100%
        item.percentOfTotalInvoiceLineItem =
            1 + Math.abs(percentOfTotalInvoiceLineItem);

        item.productRevenue =
            relativeSSPProportion *
            item.percentOfTotalInvoiceLineItem *
            totalBookingToAllocate;

        // If totalSSP nets to Zero but the items are non zero
        if (
            item.ssp.standalonePrice &&
            item.ssp.standalonePrice.type == "AS_IS" &&
            foundNonZeroSSP &&
            totalESP != 0 &&
            item.productRevenue == 0
        ) {
            item.productRevenue = item.extendedEstimatedSalePrice;
        }
    }

    static fetchBusinessKeyValues(salesOrder, soi, businessKey) {
        const salesOrderObject = (salesOrder, salesOrderItem) => [
            salesOrderItem,
            salesOrderItem.attributes,
            salesOrder.orderAttributes,
            salesOrderItem.product,
            salesOrderItem.product.attributes,
            salesOrder.customer.attributes,
            salesOrder.customer
        ];

        let picker = Utils.pickFromObject(businessKey);

        salesOrder.customer.customerName = salesOrder.customer.name;

        // Product code is an interal field that client can choose to use as business key.
        // So setting it to what it should be.
        soi.product.productCode = soi.product.code;

        let businessKeyValues = picker(salesOrderObject(salesOrder, soi));

        // delete temporary product code attribute from product object.
        delete soi.product.productCode;
        delete salesOrder.customer.customerName;

        return businessKeyValues;
    }

    /* -------
       Default Revenue Plan
       -------  */
    static buildDefaultRevenuePlan(
        revenueArrangementItem,
        salesOrder,
        orgConfig,
        customCode,
        errors
    ) {
        if (!revenueArrangementItem || !revenueArrangementItem.recognitionRule)
            throw new Error("Missing revenue arrangement item or rule");

        const d = orgConfig.find(
            (config) => config.id === "properties/calendar"
        );
        const calendarConfig = d && d.value && d.value.config;

        let productRevenue = revenueArrangementItem.productRevenue;

        const findRevenueArrangementItemByProductCode = (
            salesOrder,
            productCode,
            revenueArrangementId
        ) => {
            const revenueArrangement = salesOrder.revenueArrangement.find(
                (rai) => rai.id === revenueArrangementId
            );
            const rai = revenueArrangement.revenueArrangementItem.find(
                (revenueArrangementItem) => {
                    const soi = revenueArrangementItem.salesOrderItem;
                    if (soi && soi.product && soi.product.code == productCode) {
                        return true;
                    }
                }
            );
            return rai;
        };

        if (salesOrder.revenueArrangement) {
            const currentRevenueArrangement =
                salesOrder.revenueArrangement.find(
                    (ra) => ra.id == revenueArrangementItem.revenueArrangementId
                );

            if (currentRevenueArrangement.isRevenueDelayed) {
                productRevenue = 0;
            }
        }

        switch (revenueArrangementItem.recognitionRule.id) {
            case RecognitionRule.POINT_IN_TIME:
                revenueArrangementItem.plan = PointInTimePlan.buildPlan(
                    revenueArrangementItem.deliveryStartDate,
                    revenueArrangementItem.deliveryEndDate,
                    productRevenue,
                    revenueArrangementItem.ssp,
                    calendarConfig
                );
                break;
            case RecognitionRule.PROPORTIONAL_PERFORMANCE:
            case RecognitionRule.PERCENT_OF_COMPLETE:
                const plan = buildPlan(
                    revenueArrangementItem,
                    salesOrder,
                    orgConfig,
                    productRevenue,
                    calendarConfig
                );
                PSHelper.updateToBaseServicePlan(plan, productRevenue);
                revenueArrangementItem.plan = plan;
                break;
            case RecognitionRule.TRACK_OTHER_PRODUCT_REVENUE:
                let raiplan = [];
                const productToTrack =
                    revenueArrangementItem.ssp.productCodeToTrack;
                const baseProductRAI = findRevenueArrangementItemByProductCode(
                    salesOrder,
                    productToTrack,
                    revenueArrangementItem.revenueArrangementId
                );
                if (baseProductRAI) {
                    revenueArrangementItem.deliveryStartDate =
                        baseProductRAI.deliveryStartDate;
                    revenueArrangementItem.deliveryEndDate =
                        baseProductRAI.deliveryEndDate;
                    const itemPlan = buildPlan(
                        revenueArrangementItem,
                        salesOrder,
                        orgConfig,
                        productRevenue,
                        calendarConfig
                    );
                    raiplan = calculatePlanAmount(
                        itemPlan,
                        productRevenue,
                        baseProductRAI
                    );
                } else {
                    errors.add(Codes.SO_14, {
                        salesOrderId: salesOrder.id,
                        salesOrderItemId:
                            revenueArrangementItem.salesOrderItemId,
                        productCodeToTrack: productToTrack
                    });
                }
                revenueArrangementItem.plan = raiplan;
                break;
            default:
                revenueArrangementItem.plan = buildPlan(
                    revenueArrangementItem,
                    salesOrder,
                    orgConfig,
                    productRevenue,
                    calendarConfig,
                    customCode
                );
        }

        prefillZeroAtStart(
            revenueArrangementItem.plan,
            salesOrder,
            calendarConfig
        );
    }

    /**
     * If we have billing that is coming after the effective date of the
     * revenue arrangement then we want to add those periods in the plan
     * with 0 so that when transactions are generated they don't have any missing
     * rows in it
     * @param {*} plan
     * @param {*} salesOrder
     * @param {*} calendarConfig
     * @returns
     */

    static addBillingPeriodsToPlan(plan, salesOrder, calendarConfig) {
        if (plan.length == 0) return;

        const maxBillingDate = this.getMaxBillingDate(salesOrder);

        if (!maxBillingDate) return;

        const lastPlanActgPeriod = plan[plan.length - 1].actgPeriod;

        const maxBillingEffectivePeriod = Period.toActgPeriod(
            maxBillingDate,
            calendarConfig
        );

        if (maxBillingEffectivePeriod.period <= lastPlanActgPeriod.period)
            return;

        const periods = Period.periodsBetween(
            lastPlanActgPeriod.period,
            maxBillingEffectivePeriod.period,
            false,
            true,
            calendarConfig || { type: "default" }
        );

        if (periods.length == 0) return;

        let actgPeriod = lastPlanActgPeriod;
        for (let period of periods) {
            actgPeriod = actgPeriod.get(period);

            plan.push({
                actgPeriod,
                planAmount: 0,
                percentRecognized: 0
            });
        }
    }

    static getMaxBillingDate(salesOrder) {
        let maxBillingDate;
        // pick max billing date
        if (
            salesOrder.billingSchedule &&
            salesOrder.billingSchedule.billingScheduleItem
        ) {
            for (const bsi of salesOrder.billingSchedule.billingScheduleItem) {
                const schedule = bsi.schedule;
                if (Array.isArray(schedule) && schedule.length) {
                    const sortedByBillingDate = _.sortBy(
                        schedule,
                        (s) => s.billingDate
                    );
                    const lastBilling =
                        sortedByBillingDate[sortedByBillingDate.length - 1];
                    const lastBillingDate = lastBilling.billingDate;

                    if (!maxBillingDate) {
                        maxBillingDate = lastBillingDate;
                    } else if (lastBillingDate > maxBillingDate) {
                        maxBillingDate = lastBillingDate;
                    }
                }
            }
        }

        return maxBillingDate;
    }
}

/**
 * Debug: one console line per plan row (after default/PS plan + billing periods when applied).
 * Includes arrangement id/state, RAI, product, posting period, planAmount, effectivePeriod.
 */
export function logRevenuePlanByPeriod(salesOrderId, revenueArrangement, item) {
    const raId = revenueArrangement && revenueArrangement.id;
    const raState = revenueArrangement && revenueArrangement.state;
    const raMod =
        revenueArrangement && revenueArrangement.modificationType != null
            ? String(revenueArrangement.modificationType)
            : "";
    const soiRoot =
        item.salesOrderItem &&
        (item.salesOrderItem.rootId || item.salesOrderItem.id);
    const productCode =
        (item.salesOrderItem &&
            item.salesOrderItem.product &&
            item.salesOrderItem.product.code) ||
        "";

    if (!item || !item.plan || item.plan.length === 0) {
        console.log(
            `revenuePlanPeriod so=${salesOrderId} ra=${raId} raState=${raState} raMod=${raMod} rai=${
                item && item.id
            } soiRoot=${soiRoot} product=${productCode} note=no_plan_rows`
        );
        return;
    }

    item.plan.forEach((p, idx) => {
        const posting =
            p.actgPeriod && p.actgPeriod.period != null
                ? String(p.actgPeriod.period)
                : "";
        const eff =
            p.effectivePeriod != null && p.effectivePeriod !== ""
                ? String(p.effectivePeriod)
                : "";
        console.log(
            `revenuePlanPeriod so=${salesOrderId} ra=${raId} raState=${raState} raMod=${raMod} rai=${item.id} soiRoot=${soiRoot} product=${productCode} idx=${idx} postingPeriod=${posting} planAmount=${p.planAmount} effectivePeriod=${eff}`
        );
    });
}
